import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DropsContext } from 'contexts/DropsContext';
import { toastWarning } from 'utils/toast';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { GarantiasContext } from 'contexts/GarantiasContext';
import { GridContext } from 'contexts/GridContext';
import { useDialog, useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import EnvioModal from './Modals/EnvioModal';
import DespesaModal from './Modals/DespesaModal';
import RetornoModal from './Modals/RetornoModal';
import FabrincanteModal from './Modals/FabricanteModal';
import columnOverrides from './columnOverrides';
import getGarantiasFilterFields from './filterFields';

const Garantias = () => {
  const rotina = 'Garantias';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { openDialog, closeDialog } = useDialog();
  const { drops } = useContext(DropsContext);
  const { postDocumento } = useContext(DocumentosContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { garantias, getGarantias, getLoading } = useContext(GarantiasContext);

  const filterFields = useMemo(() => getGarantiasFilterFields(drops), [drops]);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();
    closeDialog();

    getGarantias({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: garantias?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: garantias?.order }),
    },
    {
      name: 'Enviar para o Fornecedor',
      icon: 'send',
      action: (selected) => {
        if (!Boolean(selected?.length)) {
          return toastWarning('Selecione ao menos um item');
        }
        if (selected?.some((s) => s?.status !== 'PENDENTE')) {
          return toastWarning('Selecione somente garantias pendentes');
        }
        openDialog(
          <EnvioModal itens={selected} reload={loadGrid} />,
          'Enviar para o Fornecedor'
        );
      },
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={garantias?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
  ];
  const rowActions = [
    {
      name: 'Aprovação necessária',
      icon: 'priority_high',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'PENDENTE',
    },
    {
      name: 'Aprovado',
      icon: 'check',
      color: 'primary.main',
      show: ({ row }) => row?.aprovacao === 'SIM',
    },
    {
      name: ' Reprovado',
      icon: 'close',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'NAO',
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.documento_id}`),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      menu: true,
      action: ({ row }) => navigate('/app/Garantias/Resumo', { state: row }),
    },
    {
      name: 'Registrar Código do Fabricante',
      icon: 'draw',
      menu: true,
      show: ({ row }) => Boolean(row?.documento_id) && Boolean(row?.origem_id),
      action: ({ row }) =>
        openModal(
          <FabrincanteModal
            documento_id={row?.documento_id}
            origem_id={row?.origem_id}
            callback={loadGrid}
          />
        ),
    },
    {
      name: 'Enviar Material',
      icon: 'keyboard_return',
      menu: true,
      show: ({ row }) =>
        Boolean(row?.documento_id) &&
        Boolean(row?.remessa_id) &&
        Boolean(row.produto_id),
      action: ({ row }) => {
        openDialog(
          <RetornoModal item={row} reload={loadGrid} />,
          'Enviar Material'
        );
      },
    },
    {
      name: 'Solicitar Peça',
      icon: 'build',
      menu: true,
      show: ({ row }) =>
        row?.situacao === 'ANALISE' && row?.aprovacao === 'SIM',
      action: ({ row }) => {
        const data = {
          Registros: [{ documento_id: row?.documento_id, status_id: 21 }],
        };

        postDocumento({ data, cb: loadGrid });
      },
    },
    {
      name: 'Gerar Despesa',
      icon: 'monetization_on',
      menu: true,
      show: ({ row }) => Boolean(row?.situacao === 'AUTORIZAR PAGAMENTO'),
      action: ({ row }) => {
        openModal(<DespesaModal item={row} reload={loadGrid} />, 50);
      },
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} colunas={garantias?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={garantias}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
    </Container>
  );
};

export default Garantias;
